import React, { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import './ReportInvoicedItemsForm.css';

const ReportInvoicedItemsForm = () => {
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [format, setFormat] = useState('On Screen');

  // Lab vs Admin Logic
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  // Accounts and Procedures states
  const [accounts, setAccounts] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingProcedures, setLoadingProcedures] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [proceduresError, setProceduresError] = useState(null);

  const navigate = useNavigate();

  // Function to get current month's start and end dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(start),
      endDate: formatDate(end)
    };
  };

  // Set current month dates on component mount
  useEffect(() => {
    const { startDate: currentStart, endDate: currentEnd } = getCurrentMonthDates();
    setStartDate(currentStart);
    setEndDate(currentEnd);
  }, []);

  // Load user type & labId
  useEffect(() => {
    console.log('=== Loading from localStorage ===');

    let storedLabId = localStorage.getItem('userLabId');
    let storedUserType = localStorage.getItem('userType');

    // Check sessionStorage as fallback
    if (!storedLabId) {
      storedLabId = sessionStorage.getItem('userLabId');
    }

    if (!storedUserType) {
      storedUserType = sessionStorage.getItem('userType');
    }

    // Also check for user data object
    const userDataStr = localStorage.getItem('userData') || sessionStorage.getItem('userData');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);

        if (!storedLabId && userData.labId) {
          storedLabId = userData.labId;
        }

        if (!storedUserType && userData.userType) {
          storedUserType = userData.userType;
        }
      } catch (e) {
        console.error('Error parsing userData:', e);
      }
    }

    // Set the state
    if (storedLabId) {
      setLabId(storedLabId);
    }

    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      setUserType('lab');
    } else {
      setUserType('admin');
    }

    setAuthLoaded(true);
  }, []);

  // Fetch Accounts
  useEffect(() => {
    if (!authLoaded) return;

    const fetchAccounts = async () => {
      if (!userType) return;
      if (userType === 'lab' && !labId) return;

      try {
        setLoadingAccounts(true);
        setAccountsError(null);

        let accountNames = [];

        if (userType === 'lab' && labId) {
          const apiUrl = `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`;
          const response = await axios.get(apiUrl);

          if (response.data.success) {
            const accountsData = response.data.data || [];
            accountNames = accountsData
              .filter(acc => acc && acc.account_name)
              .map(acc => acc.account_name.trim());
          } else {
            throw new Error(response.data.message || 'Failed to fetch lab accounts');
          }
        } else {
          const [hmsResponse, ddxResponse] = await Promise.all([
            axios.get("https://hms.backend.hiragroupcloud.com/api/account/account"),
            axios.get("https://hms.backend.hiragroupcloud.com/api/users")
          ]);

          const hmsAccounts = (hmsResponse.data.data || [])
            .filter(acc => acc && acc.account_name)
            .map(acc => acc.account_name.trim());

          const ddxAccounts = (ddxResponse.data.users || [])
            .filter(user => user)
            .map(user => {
              const name = user.practiceName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
              return name;
            })
            .filter(name => name);

          accountNames = [...new Set([...hmsAccounts, ...ddxAccounts])];
        }

        setAccounts(accountNames.length > 0 ? accountNames : ["No accounts found"]);
      } catch (err) {
        console.error("Error fetching accounts:", err);
        setAccountsError(err.message);
        setAccounts(["Error loading accounts"]);
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchAccounts();
  }, [userType, labId, authLoaded]);

  // Fetch Procedures
  useEffect(() => {
    if (!authLoaded) return;

    const fetchProcedures = async () => {
      if (!userType) return;
      if (userType === 'lab' && !labId) return;

      try {
        setLoadingProcedures(true);
        setProceduresError(null);

        let procedureNames = [];

        if (userType === 'lab' && labId) {
          const apiUrl = `https://hms.backend.hiragroupcloud.com/api/procedures/lab-procedures/${labId}`;
          const response = await axios.get(apiUrl);

          if (response.data.success) {
            const proceduresData = response.data.data || [];
            procedureNames = proceduresData
              .filter(proc => proc && proc.name)
              .map(proc => proc.name.trim());
          } else {
            throw new Error(response.data.message || 'Failed to fetch lab procedures');
          }
        } else {
          const apiUrl = 'https://hms.backend.hiragroupcloud.com/api/procedures';
          const response = await axios.get(apiUrl);
          const proceduresData = response.data || [];
          procedureNames = proceduresData
            .filter(proc => proc && proc.name)
            .map(proc => proc.name.trim());
        }

        const allProcedures = ["All", ...procedureNames];
        setProcedures(allProcedures.length > 1 ? allProcedures : ["All", "No procedures found"]);
      } catch (err) {
        console.error("Error fetching procedures:", err);
        setProceduresError(err.message);
        setProcedures(["All", "Error loading procedures"]);
      } finally {
        setLoadingProcedures(false);
      }
    };

    fetchProcedures();
  }, [userType, labId, authLoaded]);

  const toggleAccount = (account) => {
    setSelectedAccounts((prev) =>
      prev.includes(account)
        ? prev.filter((a) => a !== account)
        : [...prev, account]
    );
  };

  const toggleProcedure = (procedure) => {
    setSelectedProcedures((prev) =>
      prev.includes(procedure)
        ? prev.filter((p) => p !== procedure)
        : [...prev, procedure]
    );
  };

  const handleRun = async (e) => {
    e.preventDefault();

    // Get labId from localStorage if not already in state
    let currentLabId = labId;
    let currentUserType = userType;

    if (!currentLabId) {
      currentLabId = localStorage.getItem('userLabId') || sessionStorage.getItem('userLabId');
      const userDataStr = localStorage.getItem('userData') || sessionStorage.getItem('userData');
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          if (!currentLabId && userData.labId) currentLabId = userData.labId;
          if (!currentUserType && userData.userType) currentUserType = userData.userType;
        } catch (e) {
          console.error('Error parsing userData:', e);
        }
      }
    }

    const formData = {
      startDate,
      endDate,
      selectedAccounts: selectedAccounts, // Can be empty array
      selectedProcedures: selectedProcedures.length > 0 ? selectedProcedures : ["All"],
      labId: currentLabId,
      userType: currentUserType || (currentLabId ? 'lab' : 'admin')
    };

    console.log('Navigating with formData:', formData);
    navigate('/Addinvoice', { state: { formData } });
  };

  // Text shown inside the dropdown toggles
  const toggleText = (selected, placeholder) =>
    selected.length > 0
      ? selected.length <= 2
        ? selected.join(', ')
        : `${selected.length} selected`
      : placeholder;

  // Show loading while auth is being loaded
  if (!authLoaded) {
    return (
      <div className="inv-wrap">
        <h2 className="inv-title">Parameters</h2>
        <div className="inv-panel inv-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 mb-0">Loading user information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inv-wrap">
      <h2 className="inv-title">Parameters</h2>

      <form className="inv-panel" onSubmit={handleRun}>
        {/* Start Date */}
        <div className="inv-row">
          <label className="inv-label" htmlFor="inv-start">
            Start Date <span className="inv-req">*</span>
          </label>
          <div className="inv-field">
            <input
              id="inv-start"
              type="date"
              className="inv-input inv-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
        </div>

        {/* End Date */}
        <div className="inv-row">
          <label className="inv-label" htmlFor="inv-end">
            End Date <span className="inv-req">*</span>
          </label>
          <div className="inv-field">
            <input
              id="inv-end"
              type="date"
              className="inv-input inv-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        {/* Accounts Dropdown */}
        <div className="inv-row">
          <span className="inv-label">Accounts</span>
          <div className="inv-field">
            <Dropdown>
              <Dropdown.Toggle as="div" bsPrefix="inv-toggle" className="inv-input inv-select">
                {toggleText(selectedAccounts, 'Select accounts')}
              </Dropdown.Toggle>

              <Dropdown.Menu className="inv-menu">
                {loadingAccounts ? (
                  <div className="inv-msg">Loading accounts...</div>
                ) : accountsError ? (
                  <div className="inv-msg text-danger">Error: {accountsError}</div>
                ) : accounts.length === 0 ? (
                  <div className="inv-msg">No accounts found</div>
                ) : (
                  accounts.map((account, idx) => (
                    <label key={idx} className="inv-check inv-menu-item">
                      <input
                        type="checkbox"
                        checked={selectedAccounts.includes(account)}
                        onChange={() => toggleAccount(account)}
                      />
                      {account}
                    </label>
                  ))
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Account Tags */}
        <div className="inv-row">
          <label className="inv-label" htmlFor="inv-tags">Account Tags</label>
          <div className="inv-field">
            <select id="inv-tags" className="inv-input inv-select">
              <option value="">No filter</option>
            </select>
          </div>
        </div>

        {/* Inactive Selectable */}
        <div className="inv-row">
          <span className="inv-label" />
          <div className="inv-field">
            <label className="inv-check"><input type="checkbox" /> Inactive Selectable?</label>
          </div>
        </div>

        {/* Procedures Dropdown */}
        <div className="inv-row">
          <span className="inv-label">Procedures</span>
          <div className="inv-field">
            <Dropdown>
              <Dropdown.Toggle as="div" bsPrefix="inv-toggle" className="inv-input inv-select">
                {toggleText(selectedProcedures, 'Select procedures')}
              </Dropdown.Toggle>

              <Dropdown.Menu className="inv-menu">
                {loadingProcedures ? (
                  <div className="inv-msg">Loading procedures...</div>
                ) : proceduresError ? (
                  <div className="inv-msg text-danger">Error: {proceduresError}</div>
                ) : procedures.length === 0 ? (
                  <div className="inv-msg">No procedures found</div>
                ) : (
                  procedures.map((procedure, idx) => (
                    <label key={idx} className="inv-check inv-menu-item">
                      <input
                        type="checkbox"
                        checked={selectedProcedures.includes(procedure)}
                        onChange={() => toggleProcedure(procedure)}
                      />
                      {procedure}
                    </label>
                  ))
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Options */}
        <div className="inv-row inv-row-top">
          <span className="inv-label">Options</span>
          <div className="inv-field inv-col">
            <label className="inv-check"><input type="checkbox" /> Include General Invoices</label>
            <label className="inv-check"><input type="checkbox" /> Include Finance Charge</label>
            <label className="inv-check"><input type="checkbox" /> Exclude Attributes when price is zero</label>
          </div>
        </div>

        {/* Format */}
        <div className="inv-row">
          <label className="inv-label" htmlFor="inv-format">
            Format <span className="inv-req">*</span>
          </label>
          <div className="inv-field">
            <select
              id="inv-format"
              className="inv-input inv-select"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              <option>On Screen</option>
              <option>CSV (Excel)</option>
            </select>
          </div>
        </div>

        {/* Run Button */}
        <div className="inv-row">
          <span className="inv-label" />
          <div className="inv-field">
            <button
              type="submit"
              className="inv-run"
              disabled={loadingAccounts || loadingProcedures}
            >
              {loadingAccounts || loadingProcedures ? 'Loading...' : <><FaSearch /> Run</>}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ReportInvoicedItemsForm;